import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { SecurityService } from '@wsphere/staff/web';
import { ContractLedger, Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, of, switchMap } from 'rxjs';
import { ContractLedgerWebService } from '../../../warranty/services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-contract-ledger-card',
  templateUrl: './contract-ledger-card.component.html',
  standalone: false,
})
export class ContractLedgerCardComponent {
  constructor(
    private securitySvc: SecurityService,
    private contractLedgerSvc: ContractLedgerWebService
  ) {}

  @PropertyListener('warranty') warranty$ = new BehaviorSubject<Warranty.Model | null>(null);
  @Input() warranty?: Warranty.Model | null;

  // The ledger surfaces financial detail — funding, disbursements, party balances — beyond what
  // wrt_ViewWarranty alone is meant to expose, so it also requires py_ViewSales (VS-1283).
  canView$ = this.warranty$.pipe(
    switchMap(warranty => {
      const permissionKeys = warranty?.distributor?.permissionKey ? [warranty.distributor.permissionKey] : null;
      return combineLatest([
        this.securitySvc.hasAccess('wrt_ViewWarranty', permissionKeys),
        this.securitySvc.hasAccess('py_ViewSales', permissionKeys),
      ]);
    }),
    map(([canViewWarranty, canViewSales]) => canViewWarranty && canViewSales)
  );

  ledger$ = this.warranty$.pipe(
    switchMap(warranty => {
      if (!warranty) return of(null);
      return this.contractLedgerSvc.retrieve(warranty.id).catch(() => null);
    })
  );

  partyType = ContractLedger.PartyType;
  recipientSource = ContractLedger.RecipientSource;

  describeParty(party: ContractLedger.Party) {
    // A named party shows its role too (e.g. "HomeGauge (Insurer)") — a name alone can't tell one
    // identity's roles apart.
    const role = ContractLedger.partyTypeLabel(party.type);
    if (!party.partyName) return role;
    return party.type === this.partyType.SELLER ? party.partyName : `${party.partyName} (${role})`;
  }

  describeSource(source: ContractLedger.SourceReference) {
    const type = source.type.replace(/_/g, ' ');
    const label = type.charAt(0) + type.slice(1).toLowerCase();
    return source.id ? `${label} (${source.id})` : label;
  }

  /** The Contract Amount Due total's own breakdown — every component, summed per party (e.g.
   * Insurer, Tax Authority, Seller) rather than listed as raw premium/fee/tax/markup rows, which
   * can run long and doesn't say much on its own without the party each line is owed to. */
  componentsByParty(ledger: ContractLedger.RetrieveContractLedgerQueryResponse) {
    return this.groupByParty(ledger.components);
  }

  /** The Net Funding Received total's own breakdown, grouped the same way as componentsByParty. */
  fundingByParty(ledger: ContractLedger.RetrieveContractLedgerQueryResponse) {
    return this.groupByParty(ledger.funding);
  }

  /** The Customer Refund Activity total's own breakdown — grouped the same way, though this
   * mostly collapses to one CUSTOMER row since a contract has a single customer. */
  customerRefundsByParty(ledger: ContractLedger.RetrieveContractLedgerQueryResponse) {
    return this.groupByParty(ledger.disbursements.filter(d => d.party.type === this.partyType.CUSTOMER));
  }

  /** The Seller Clawback Receivable total's own breakdown — one row per seller currently
   * overpaid, each contributing max(0, -outstandingPayable) to that total (see
   * retrieve-party-balances.handler.ts), so this shows the same positive amount being summed
   * rather than the underlying negative balance itself. */
  sellerClawbackContributions(ledger: ContractLedger.RetrieveContractLedgerQueryResponse) {
    return ledger.partyBalances.parties
      .filter(p => p.party.type === this.partyType.SELLER && p.outstandingPayable < 0)
      .map(p => ({ party: p.party, amount: -p.outstandingPayable }));
  }

  private groupByParty<T extends { party: ContractLedger.Party; amount: number }>(items: T[]) {
    const totals = new Map<string, { party: ContractLedger.Party; amount: number }>();

    for (const item of items) {
      const key = `${item.party.type}:${item.party.partyId ?? ''}`;
      const existing = totals.get(key);
      if (existing) existing.amount += item.amount;
      else totals.set(key, { party: item.party, amount: item.amount });
    }

    return Array.from(totals.values());
  }
}
